import {
  type FilterDisplayConfig,
  type FilterMapping,
  FILTER_DISPLAY_CONFIG,
  FILTER_MAPPINGS,
} from './filterMapping.config';
import {FilterOptionProps} from './MyTeamsContent.types';

// Define local interface to match component expectations
interface FilterRangeDetails {
  options?: FilterOptionProps[];
  isFilterEnabled?: boolean;
  minDigit: number;
  maxDigit: number;
  resultRangeState: string;
  filterByText: string;
  aboveValue: string;
  belowValue: string;
  isError: boolean;
  ValidationError?: boolean;
}

export const processRangeFilter = (
  details: FilterRangeDetails,
  filterType: string,
): Record<string, any> => {
  const mapping = FILTER_MAPPINGS[filterType as keyof typeof FILTER_MAPPINGS];
  if (!mapping) {
    return {};
  }

  // Define clearKeys helper to initialize all keys with undefined
  const getClearKeys = (map: any): Record<string, any> => {
    const cleared: Record<string, any> = {};
    if (map.optionMappings) {
      Object.values(map.optionMappings).forEach((optMapping: any) => {
        cleared[optMapping.minKey] = undefined;
        cleared[optMapping.maxKey] = undefined;
      });
    } else {
      if (map.minKey) cleared[map.minKey] = undefined;
      if (map.maxKey) cleared[map.maxKey] = undefined;
    }
    return cleared;
  };

  if (!details.isFilterEnabled || details.isError) {
    return getClearKeys(mapping);
  }

  // Initialize result with all keys cleared to ensure mutual exclusivity
  const result: Record<string, any> = getClearKeys(mapping);

  // Handle Filter Range special case with options
  if (mapping.optionMappings && details.options) {
    const selectedOption = details.options.find(opt => opt.selected);
    if (!selectedOption) return result;

    const optionMapping = mapping.optionMappings[selectedOption.id];
    if (!optionMapping) return result;

    // Apply range logic using the option-specific keys
    applyRangeLogic(
      result,
      details.resultRangeState,
      details.aboveValue,
      details.belowValue,
      optionMapping,
    );
  } else {
    // Standard mapping
    applyRangeLogic(
      result,
      details.resultRangeState,
      details.aboveValue,
      details.belowValue,
      mapping,
    );
  }

  return result;
};

const applyRangeLogic = (
  result: Record<string, any>,
  rangeState: string,
  aboveValue: string,
  belowValue: string,
  mapping: {minKey: string; maxKey: string},
) => {
  switch (rangeState) {
    case 'above':
      if (aboveValue) result[mapping.minKey] = aboveValue;
      break;
    case 'below':
      if (belowValue) result[mapping.maxKey] = belowValue;
      break;
    case 'between':
      if (aboveValue) result[mapping.minKey] = aboveValue;
      if (belowValue) result[mapping.maxKey] = belowValue;
      break;
  }
};

// Functional approach alternative
export const createRangeFilterProcessor =
  (mapping: FilterMapping) => (details: FilterRangeDetails) =>
    processRangeFilter(details, mapping as any);

// New utility functions for MyTeamsContent.tsx

export const countActiveRangeFilters = (filters: any): string[] => {
  return Object.entries(FILTER_DISPLAY_CONFIG)
    .filter(
      ([_, config]) =>
        filters[config.minKey] !== undefined ||
        filters[config.maxKey] !== undefined,
    )
    .map(([_, config]) => config.countLabel);
};

export const generateRangeFilterTags = (filters: any): string[] => {
  return Object.entries(FILTER_DISPLAY_CONFIG)
    .map(([_, config]) => generateFilterTag(filters, config))
    .filter((tag): tag is string => tag !== null);
};

const generateFilterTag = (
  filters: any,
  config: FilterDisplayConfig,
): string | null => {
  const hasMin = filters[config.minKey] !== undefined;
  const hasMax = filters[config.maxKey] !== undefined;

  if (!hasMin && !hasMax) return null;

  let text = config.label + ': ';
  if (hasMin && hasMax) {
    text += `${filters[config.minKey]} - ${filters[config.maxKey]}`;
  } else if (hasMin) {
    text += `Above ${filters[config.minKey]}`;
  } else if (hasMax) {
    text += `Below ${filters[config.maxKey]}`;
  }

  return text;
};

export const reconstructRangeFilterState = (
  filters: any,
  filterType: string,
): FilterRangeDetails | null => {
  const config =
    FILTER_DISPLAY_CONFIG[filterType as keyof typeof FILTER_DISPLAY_CONFIG];
  if (!config) return null;
  if (filterType === 'filterRange' && config.optionMappings) {
    const selectedOptionId =
      filters.newClientsAbove !== undefined ||
      filters.newClientsBelow !== undefined
        ? 'new_clients'
        : filters.qualifiedClientsAbove !== undefined ||
          filters.qualifiedClientsBelow !== undefined
        ? 'q_new_clients'
        : 'new_clients';
    const optionMapping =
      config.optionMappings[
        selectedOptionId as keyof typeof config.optionMappings
      ];
    if (!optionMapping) return null;
    const hasMin = filters[optionMapping.minKey] !== undefined;
    const hasMax = filters[optionMapping.maxKey] !== undefined;

    if (!hasMin && !hasMax) return null;

    let resultRangeState = 'above';
    if (hasMin && hasMax) {
      resultRangeState = 'between';
    } else if (hasMax && !hasMin) {
      resultRangeState = 'below';
    }

    return {
      options: [
        {
          text: 'New Clients',
          id: 'new_clients',
          selected: selectedOptionId === 'new_clients',
          defaultVal: true,
        },
        {
          text: 'Qualified New Clients',
          id: 'q_new_clients',
          selected: selectedOptionId === 'q_new_clients',
          defaultVal: false,
        },
      ],
      isFilterEnabled: true,
      minDigit: config.minDigit,
      maxDigit: config.maxDigit,
      resultRangeState,
      filterByText: config.filterByText,
      aboveValue: filters[optionMapping.minKey] || '',
      belowValue: filters[optionMapping.maxKey] || '',
      isError: false,
    };
  }

  const hasMin = filters[config.minKey] !== undefined;
  const hasMax = filters[config.maxKey] !== undefined;

  if (!hasMin && !hasMax) return null;
  let resultRangeState = 'above';
  if (hasMin && hasMax) {
    resultRangeState = 'between';
  } else if (hasMax && !hasMin) {
    resultRangeState = 'below';
  }

  return {
    options: [],
    isFilterEnabled: true,
    minDigit: config.minDigit,
    maxDigit: config.maxDigit,
    resultRangeState,
    filterByText: config.filterByText,
    aboveValue: filters[config.minKey] || '',
    belowValue: filters[config.maxKey] || '',
    isError: false,
  };
};
